import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, ArrowRight, Shield, Clock, Users, Loader2, CreditCard } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const PLANS = [
  {
    id: "estandar",
    name: "ESTÁNDAR",
    price: 19.99,
    popular: false,
    color: "white",
    features: [
      "DS-160 pre-llenado automático",
      "Asistente IA 24/7",
      "Guía paso a paso",
      "Soporte por email"
    ]
  },
  {
    id: "premium",
    name: "PREMIUM",
    price: 49.99,
    popular: true,
    color: "emerald",
    features: [
      "Todo del plan Estándar",
      "Asesor personal asignado",
      "Llamada de verificación",
      "DS-160 ejecutado por experto",
      "Soporte prioritario"
    ]
  },
  {
    id: "vip",
    name: "VIP ENTERPRISE",
    price: 99.99,
    popular: false,
    color: "white",
    badge: "Solo quedan 3 — Oferta limitada",
    features: [
      "ADELANTO DE CITA CONSULAR INCLUIDA (promedio 300 días)",
      "BONUS GRATIS: 3 días Orlando + 3 días Miami (PAQUETE PARA UNA PAREJA)",
      "BONO ÚNICO: $500 USD de crédito en +3 millones de hoteles (Solo quedan 3 — Oferta limitada)",
      "Todo del plan Premium",
      "Simulación de entrevista IA",
      "Soporte prioritario 24/7",
      "Oficial dedicado"
    ]
  }
];

const ReferralLandingPage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [referrerName, setReferrerName] = useState("");
  const [isValid, setIsValid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [numPeople, setNumPeople] = useState(1);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: ""
  });

  useEffect(() => {
    validateCode();
  }, [code]);

  const validateCode = async () => {
    try {
      const response = await axios.get(`${API}/api/referrals/validate/${code}`);
      if (response.data.valid) {
        setIsValid(true);
        setReferrerName(response.data.referrer_name);
        // Guardar código en localStorage para usarlo en checkout
        localStorage.setItem("referral_code", code.toUpperCase());
      } else {
        setIsValid(false);
      }
    } catch (error) {
      setIsValid(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setShowCheckoutForm(true);
    // Scroll to form
    setTimeout(() => {
      document.getElementById("checkout-form")?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const handleDirectCheckout = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      toast.error("Por favor completa nombre y email");
      return;
    }

    setCheckoutLoading(true);
    
    try {
      // Crear sesión de checkout directa con Stripe
      const response = await axios.post(`${API}/api/payments/referral-checkout`, {
        plan_type: selectedPlan.id,
        referral_code: code.toUpperCase(),
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone || null,
        num_people: numPeople
      });

      if (response.data.checkout_url) {
        // Redirigir a Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error("No se recibió URL de pago");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error(error.response?.data?.detail || "Error al procesar el pago. Intenta de nuevo.");
      setCheckoutLoading(false);
    }
  };

  const handleLogin = () => {
    navigate(`/login?ref=${code}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!isValid) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">❌</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Código no válido</h1>
          <p className="text-gray-600 mb-6">
            El código de referido "{code}" no existe o ha expirado.
          </p>
          <Button onClick={() => navigate("/")} className="bg-emerald-600 hover:bg-emerald-700">
            Ir al inicio
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-emerald-50">
      {/* Header */}
      <header className="py-4 px-6 flex justify-between items-center max-w-6xl mx-auto">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center">
            <span className="text-white font-bold text-lg">D</span>
          </div>
          <span className="font-bold text-xl text-gray-900">Divídelo</span>
        </div>
        <Button variant="outline" onClick={handleLogin}>
          Ya tengo cuenta
        </Button>
      </header>

      {/* Hero Section */}
      <main className="max-w-5xl mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          {/* Referral Badge */}
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Users className="w-4 h-4" />
            Invitado por {referrerName}
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Tu Visa Americana<br />
            <span className="text-emerald-600">Más Cerca que Nunca</span>
          </h1>
          
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-4">
            Prepara tu DS-160 con asistencia experta. Más de 5,000 clientes han confiado en nosotros.
          </p>
          
          <p className="text-emerald-600 font-medium">
            👇 Selecciona tu plan y paga directamente
          </p>
        </motion.div>

        {/* Plans Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid md:grid-cols-3 gap-6 mb-12"
        >
          {PLANS.map((plan) => (
            <div 
              key={plan.id}
              className={`rounded-2xl p-6 shadow-sm transition-all cursor-pointer ${
                plan.popular 
                  ? "bg-emerald-600 text-white transform md:-translate-y-2 shadow-lg" 
                  : "bg-white border border-gray-200 hover:border-emerald-300 hover:shadow-md"
              } ${selectedPlan?.id === plan.id ? "ring-4 ring-emerald-400" : ""}`}
              onClick={() => handleSelectPlan(plan)}
            >
              <div className="flex justify-between items-start mb-1">
                <h3 className={`font-semibold ${plan.popular ? "text-white" : "text-gray-900"}`}>
                  {plan.name}
                </h3>
                {plan.popular && (
                  <span className="bg-white/20 text-xs px-2 py-1 rounded-full">Popular</span>
                )}
              </div>
              <p className={`text-3xl font-bold mb-4 ${plan.popular ? "text-white" : "text-gray-900"}`}>
                ${plan.price}
              </p>
              <ul className={`space-y-3 text-sm mb-6 ${plan.popular ? "text-emerald-50" : "text-gray-600"}`}>
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <CheckCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                      plan.popular ? "text-emerald-300" : "text-emerald-500"
                    }`} />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <Button 
                className={`w-full ${
                  plan.popular 
                    ? "bg-white text-emerald-600 hover:bg-emerald-50" 
                    : "bg-emerald-600 text-white hover:bg-emerald-700"
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectPlan(plan);
                }}
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Comprar Ahora
              </Button>
            </div>
          ))}
        </motion.div>

        {/* Checkout Form */}
        {showCheckoutForm && selectedPlan && (
          <motion.div
            id="checkout-form"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-md mx-auto"
          >
            <div className="bg-white rounded-2xl border-2 border-emerald-200 p-8 shadow-lg">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CreditCard className="w-8 h-8 text-emerald-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Completa tu Compra</h2>
                <p className="text-gray-600 mt-1">
                  Plan <strong>{selectedPlan.name}</strong> - <strong>${selectedPlan.price}</strong> por persona
                </p>
                <p className="text-sm text-emerald-600 mt-2">
                  Referido por: {referrerName}
                </p>
              </div>

              <form onSubmit={handleDirectCheckout} className="space-y-4">
                {/* Number of People Selector */}
                <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                  <Label className="text-emerald-800 font-medium">¿Cuántas personas aplicarán?</Label>
                  <p className="text-xs text-emerald-600 mb-3">Selecciona el número de personas que tramitarán la visa</p>
                  <div className="flex items-center justify-center gap-4">
                    <button
                      type="button"
                      onClick={() => setNumPeople(Math.max(1, numPeople - 1))}
                      className="w-10 h-10 rounded-full bg-white border-2 border-emerald-300 text-emerald-600 font-bold text-xl hover:bg-emerald-100 transition-colors"
                    >
                      -
                    </button>
                    <div className="text-center">
                      <span className="text-3xl font-bold text-emerald-700">{numPeople}</span>
                      <p className="text-xs text-emerald-600">{numPeople === 1 ? "persona" : "personas"}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setNumPeople(Math.min(10, numPeople + 1))}
                      className="w-10 h-10 rounded-full bg-white border-2 border-emerald-300 text-emerald-600 font-bold text-xl hover:bg-emerald-100 transition-colors"
                    >
                      +
                    </button>
                  </div>
                  {numPeople > 1 && (
                    <div className="mt-3 p-2 bg-white rounded-lg text-center">
                      <p className="text-sm text-gray-600">
                        Total: <strong className="text-emerald-700">${(selectedPlan.price * numPeople).toFixed(2)}</strong>
                      </p>
                      <p className="text-xs text-gray-500">
                        ({numPeople} × ${selectedPlan.price})
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="name">Nombre del Titular *</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Nombre del jefe/a de familia"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="mt-1"
                  />
                  {numPeople > 1 && (
                    <p className="text-xs text-gray-500 mt-1">
                      Los datos de las otras {numPeople - 1} personas se agregarán después del pago
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="tu@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Teléfono (opcional)</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+52 123 456 7890"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-emerald-600 hover:bg-emerald-700 py-6 text-lg"
                  disabled={checkoutLoading}
                >
                  {checkoutLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Procesando...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Pagar ${(selectedPlan.price * numPeople).toFixed(2)}
                      {numPeople > 1 && ` (${numPeople} personas)`}
                    </>
                  )}
                </Button>

                <p className="text-xs text-gray-500 text-center">
                  Serás redirigido a Stripe para completar el pago de forma segura.
                  <br />
                  Después del pago, recibirás tus credenciales por email.
                </p>
              </form>
            </div>
          </motion.div>
        )}

        {/* Trust Badges */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="flex flex-wrap justify-center gap-8 mt-16 text-center"
        >
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-2">
              <Shield className="w-6 h-6 text-emerald-600" />
            </div>
            <p className="text-sm font-medium text-gray-900">100% Seguro</p>
            <p className="text-xs text-gray-500">Datos encriptados</p>
          </div>
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-2">
              <Clock className="w-6 h-6 text-emerald-600" />
            </div>
            <p className="text-sm font-medium text-gray-900">Rápido</p>
            <p className="text-xs text-gray-500">Listo en 24-48hrs</p>
          </div>
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-2">
              <Users className="w-6 h-6 text-emerald-600" />
            </div>
            <p className="text-sm font-medium text-gray-900">+5,000</p>
            <p className="text-xs text-gray-500">Clientes felices</p>
          </div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="text-center py-8 text-sm text-gray-500">
        © 2026 Divídelo. Todos los derechos reservados.
      </footer>
    </div>
  );
};

export default ReferralLandingPage;
